import type { SVGProps } from "react";
import { User, Users } from "lucide-react";

/** Who a session is with. */
export type SubjectKind = "individual" | "couple" | "group";

/**
 * Three people, the middle one in front — lucide has one and two, but nothing
 * that reads as "more than a couple", and a group toggle showing two people
 * would say the same as the couple one beside it.
 */
export function GroupOfPeopleIcon({
    size = 24,
    className,
    ...rest
}: SVGProps<SVGSVGElement> & { size?: number | string }) {
    // Takes what a lucide icon takes (`size`, `className`, aria props), so it
    // drops in wherever the two-person `UsersRound` used to stand for a group.
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
            {...rest}
        >
            <circle cx="12" cy="7" r="3" />
            <path d="M6.5 20a5.5 5.5 0 0 1 11 0" />
            <circle cx="4.5" cy="9.5" r="2" />
            <path d="M1.5 18.5a3 3 0 0 1 4-2.8" />
            <circle cx="19.5" cy="9.5" r="2" />
            <path d="M22.5 18.5a3 3 0 0 0-4-2.8" />
        </svg>
    );
}

/** One person, two, or several: the mark for each kind of session. */
export default function SubjectKindIcon({
    kind,
    className = "size-5",
}: {
    kind: SubjectKind;
    className?: string;
}) {
    if (kind === "group") return <GroupOfPeopleIcon className={className} />;
    if (kind === "couple") return <Users className={className} aria-hidden="true" />;
    return <User className={className} aria-hidden="true" />;
}
